import './polyfills';
import './integrations';
import './globalStyleImports';

import {LicenseInfo} from '@mui/x-license';
import {NuqsAdapter} from 'nuqs/adapters/react';
import React from 'react';
import {Provider} from 'react-redux';
import {BrowserRouter} from 'react-router-dom';
import {enableBatching} from 'redux-batched-actions';
import {Loader} from 'semantic-ui-react';

import {apolloClient} from './apolloClient';
import config, {envIsIntegration, urlPrefixed} from './config';
import {DualModeReactRenderer} from './DualModeReactRenderer';
import {logStartupFeatureFlagEvaluations} from './logStartupFeatureFlagEvaluations';
import {ConcurrentRenderModeSwitcher} from './RenderModeContext';
import {AppWithRoutes} from './routes/AppWithRoutes';
import {apolloClient2, debugCollector, store} from './setup';
import {ApolloProvider} from './state/graphql/apolloWithAbort';
import uninstallServiceWorker from './uninstallServiceWorker';
import {Apollo2Context} from './util/apollo2-hooks';
import {DatadogProvider} from './util/datadog/DatadogProvider';

// Initialize MUI X license for Pro components used in Reports, etc.
LicenseInfo.setLicenseKey(
  '6bfd64b5485eccfeba7cbe13ff898697Tz0xMjQwNjYsRT0xNzk5NjI1NTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTEsQVQ9bXVsdGksS1Y9Mg=='
);

if (config.ENVIRONMENT_IS_PRIVATE) {
  // this obfuscates the original log call locations, so only use it
  // in local, when Sentry et all aren't available
  debugCollector.instrument();
}

uninstallServiceWorker();
logStartupFeatureFlagEvaluations();

async function fetchOverridesWithRetry(retries = 5): Promise<Response> {
  try {
    // oxlint-disable-next-line wandb/no-unprefixed-urls
    const resp = await fetch(
      `${config.LOCAL_TEST_FIXTURE_SERVER_HOST}/app-config-override`,
      {
        method: 'GET',
      }
    );

    if (resp.ok) {
      return resp;
    }

    throw new Error(`Failed to fetch config overrides: ${resp.status}`);
  } catch (e) {
    if (retries > 0) {
      return fetchOverridesWithRetry(retries - 1);
    }
    throw e;
  }
}

const ConfigOverrideProvider: React.FC<{
  children?: React.ReactNode;
}> = props => {
  const [configOverridesLoading, setConfigOverridesLoading] =
    React.useState<boolean>(
      envIsIntegration && !!config.LOCAL_TEST_FIXTURE_SERVER_HOST
    );
  React.useEffect(() => {
    // This is used by integration tests to override config values
    if (configOverridesLoading) {
      // oxlint-disable-next-line wandb/no-unprefixed-urls
      fetchOverridesWithRetry().then(async response => {
        const overrides = await response.json();
        Object.assign(config, overrides);
        setConfigOverridesLoading(false);
      });
    }
  }, [configOverridesLoading, setConfigOverridesLoading]);

  if (configOverridesLoading) {
    return <Loader name="config-override-loader" />;
  }

  return <>{props.children}</>;
};

const renderer = new DualModeReactRenderer(
  document.getElementById('root')!,
  (AppWithRoutes: any) => (
    <NuqsAdapter>
      <ConfigOverrideProvider>
        <Provider store={store}>
          <DatadogProvider>
            <ApolloProvider client={apolloClient}>
              <Apollo2Context.Provider value={apolloClient2}>
                <ConcurrentRenderModeSwitcher>
                  <BrowserRouter basename={urlPrefixed(undefined, false)}>
                    <AppWithRoutes />
                  </BrowserRouter>
                </ConcurrentRenderModeSwitcher>
              </Apollo2Context.Provider>
            </ApolloProvider>
          </DatadogProvider>
        </Provider>
      </ConfigOverrideProvider>
    </NuqsAdapter>
  )
);

renderer.render(AppWithRoutes);

// @ts-expect-error This is a developer handle for switching between render modes
window.setRenderMode = (mode: 'concurrent' | 'legacy') => {
  renderer.changeRenderMode(mode);
};

const hot = import.meta.hot;
if (hot) {
  hot.accept('./reducers', updatedCreateRootReducer => {
    store.replaceReducer(enableBatching(updatedCreateRootReducer()));
  });
  hot.accept('./routes', async ({default: NextApp}) => {
    renderer.render(NextApp);
  });
}
